import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ChevronDown, Menu, Moon, Sun, X } from "lucide-react";
import { ThemeContext } from "../../context/ThemeContext";
import { audienceFromLocation, audienceSites } from "../audience/audienceSites";
import usePrimaryNavSection from "../../hooks/usePrimaryNavSection";
import "./Navbar.css";

const welcomeNavigation = [
  { to: "/scientists", label: "Scientists" },
  { to: "/investors", label: "Investors" },
  { to: "/patients", label: "Patients & families" },
];

export default function Navbar() {
  const { darkMode, setDarkMode } = useContext(ThemeContext);
  const location = useLocation();
  const audience = audienceFromLocation(location);
  const site = audienceSites[audience];
  const navigation = site?.navigation || welcomeNavigation;
  const investorSection = usePrimaryNavSection({
    enabled: location.pathname === "/investors",
    defaultKey: location.hash.slice(1) || "vision",
    selector: "[data-investor-section]",
    dataKey: "investorSection",
    activationOffset: 128,
  });
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const audienceMenu = useRef(null);
  const mobileToggle = useRef(null);
  const contactHref = (source) => `${site?.base || ""}/contact?source=${source}`;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    if (audienceMenu.current) audienceMenu.current.open = false;
  }, [location.key, location.pathname, location.search, location.hash]);

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      if (audienceMenu.current?.open) {
        audienceMenu.current.open = false;
        audienceMenu.current.querySelector("summary")?.focus();
      } else if (open) {
        setOpen(false);
        mobileToggle.current?.focus();
      }
    };
    const closeOnOutside = (event) => {
      if (audienceMenu.current && !audienceMenu.current.contains(event.target)) audienceMenu.current.open = false;
    };
    document.addEventListener("keydown", closeOnEscape);
    document.addEventListener("pointerdown", closeOnOutside);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.removeEventListener("pointerdown", closeOnOutside);
    };
  }, [open]);

  const isActive = ({ to, also = [] }) => {
    const [pathname, anchor] = to.split("#");
    if (anchor) return location.pathname === pathname && investorSection === anchor;
    return location.pathname === to || also.includes(location.pathname) ||
      (to === "/scientists/solutions" && location.pathname === "/solutions");
  };

  const links = navigation.map((item) => (
    <Link key={item.to} to={item.to} aria-current={isActive(item) ? (item.to.includes("#") ? "location" : "page") : undefined}>{item.label}</Link>
  ));
  const themeButton = (mobile = false) => (
    <button className="site-theme-toggle" type="button" onClick={() => setDarkMode(!darkMode)} aria-label={`Switch to ${darkMode ? "light" : "dark"} theme`}>
      {darkMode ? <Sun size={17} aria-hidden="true" /> : <Moon size={17} aria-hidden="true" />}
      {mobile && <span>{darkMode ? "Light mode" : "Dark mode"}</span>}
    </button>
  );

  return (
    <header className={`site-header${scrolled || open ? " site-header-solid" : ""}`}>
      <div className="site-header-inner">
        <Link to={site?.base || "/"} className="site-logo" aria-label="Cornucopia home">
          <img src="/Logo.svg" alt="" className="site-logo-light" width="152" height="42" />
          <img src="/Logo-dark.svg" alt="" className="site-logo-dark" width="152" height="42" />
        </Link>
        {site && (
          <details className="site-audience-menu" ref={audienceMenu}>
            <summary aria-label={`Change audience: ${site.label}`}><span>{site.label}</span><ChevronDown size={13} aria-hidden="true" /></summary>
            <nav aria-label="Switch audience">
              {Object.entries(audienceSites).map(([id, target]) => (
                <Link key={id} to={target.base} aria-current={id === audience ? "true" : undefined}>{target.fullLabel}</Link>
              ))}
              <Link to="/" className="site-welcome-link">Back to welcome</Link>
            </nav>
          </details>
        )}
        <nav className="site-desktop-navigation" aria-label="Primary navigation">
          <div className="site-page-links">{links}</div>
          <Link className="site-contact" to={contactHref("navbar")} data-cta="discuss-project" data-solution-id={audience === "scientist" ? "workflow-review" : undefined}>
            {site?.cta || "Get in touch"}<ArrowRight size={15} aria-hidden="true" />
          </Link>
          {themeButton()}
        </nav>
        <button ref={mobileToggle} type="button" className="site-mobile-toggle" aria-label={open ? "Close navigation" : "Open navigation"} aria-expanded={open} aria-controls="mobile-navigation" onClick={() => { setOpen(!open); if (audienceMenu.current) audienceMenu.current.open = false; }}>
          {open ? <X size={21} aria-hidden="true" /> : <Menu size={21} aria-hidden="true" />}
        </button>
      </div>
      {open && (
        <nav id="mobile-navigation" className="site-mobile-navigation" aria-label="Mobile navigation">
          <div className="site-mobile-page-links">{links}</div>
          <div className="site-mobile-actions">
            <Link className="site-contact" to={contactHref("mobile-navbar")} data-cta="discuss-project">{site?.cta || "Get in touch"}<ArrowRight size={16} aria-hidden="true" /></Link>
            {themeButton(true)}
          </div>
        </nav>
      )}
    </header>
  );
}
